import { Signal, signal } from '@angular/core';
import { SessionStorageService } from '@services/session-storage.service';
import {
  createStateRouteEntry,
  isInternalStateRouteUrl,
  isContextualStateRoute,
  isStateRouteTransient,
  MAX_STATE_ROUTE_DEPTH,
  normalizeStateRoutePath,
  resolveStateRouteBranch,
  resolveStateRouteParentPaths,
  STATE_ROUTE_ROOT,
} from '@state-routing/config/state-routing.config';
import { StateRouteEntry } from '@state-routing/models/state-route.models';

interface PersistedStateRouteState {
  readonly ownerUserId: number | null;
  readonly routes: readonly StateRouteEntry[];
  readonly sessionId: string;
}

export class StateRouteStore {
  private static readonly STORAGE_KEY: string = 'state-route-stack:v1';

  private readonly routesSignal = signal<readonly StateRouteEntry[]>([createStateRouteEntry(STATE_ROUTE_ROOT)]);
  private ownerUserId: number | null | undefined;
  private sessionIdValue: string = this.createSessionId();

  public constructor(private readonly sessionStorage: SessionStorageService) {
    this.restore();
  }

  public get routes(): Signal<readonly StateRouteEntry[]> {
    return this.routesSignal.asReadonly();
  }

  public get sessionId(): string {
    return this.sessionIdValue;
  }

  public bindOwner(userId: number | null): boolean {
    if (this.ownerUserId === undefined) {
      this.ownerUserId = userId;
      this.persist();
      return false;
    }
    if (this.ownerUserId === userId) return false;

    this.ownerUserId = userId;
    this.sessionIdValue = this.createSessionId();
    this.routesSignal.set([createStateRouteEntry(STATE_ROUTE_ROOT)]);
    this.persist();
    return true;
  }

  public record(url: string): void {
    if (isStateRouteTransient(url)) return;

    const entry: StateRouteEntry = createStateRouteEntry(url);
    const currentRoutes: readonly StateRouteEntry[] = this.routesSignal();
    const existingIndex: number = currentRoutes.findIndex(
      (route: StateRouteEntry): boolean => route.path === entry.path,
    );
    if (entry.path === STATE_ROUTE_ROOT) {
      this.setRoutes([entry]);
      return;
    }

    const precedingRoutes: readonly StateRouteEntry[] = existingIndex >= 0
      ? currentRoutes.slice(0, existingIndex)
      : currentRoutes;
    const currentPath: string = precedingRoutes[precedingRoutes.length - 1]?.path ?? STATE_ROUTE_ROOT;
    if (isContextualStateRoute(entry.path, currentPath)) {
      this.setRoutes([...precedingRoutes, entry]);
      return;
    }

    const parentPaths: readonly string[] = resolveStateRouteParentPaths(entry.path);
    const targetBranch: string = resolveStateRouteBranch(entry.path);
    const currentBranch: string = resolveStateRouteBranch(currentPath);
    const baseRoutes: readonly StateRouteEntry[] = targetBranch === currentBranch
      ? this.resolveSameBranchBase(precedingRoutes, parentPaths)
      : [createStateRouteEntry(STATE_ROUTE_ROOT)];
    const parentEntries: StateRouteEntry[] = parentPaths
      .filter((parentPath: string): boolean => !baseRoutes.some(
        (route: StateRouteEntry): boolean => route.path === parentPath,
      ))
      .map((parentPath: string): StateRouteEntry => createStateRouteEntry(parentPath));
    this.setRoutes([...baseRoutes, ...parentEntries, entry]);
  }

  public replaceCurrent(url: string): boolean {
    if (isStateRouteTransient(url)) return false;

    const currentRoutes: readonly StateRouteEntry[] = this.routesSignal();
    if (!currentRoutes.length) return false;

    const nextRoutes: StateRouteEntry[] = [...currentRoutes];
    nextRoutes[nextRoutes.length - 1] = createStateRouteEntry(url);
    this.setRoutes(nextRoutes);
    return true;
  }

  public resolvePreviousRouteUrl(currentUrl: string, targetUrl: string): string | null {
    const currentRoutes: readonly StateRouteEntry[] = this.routesSignal();
    if (currentRoutes.length < 2) return null;

    const currentEntry: StateRouteEntry | undefined = currentRoutes[currentRoutes.length - 1];
    const previousEntry: StateRouteEntry | undefined = currentRoutes[currentRoutes.length - 2];
    return currentEntry?.path === normalizeStateRoutePath(currentUrl)
      && previousEntry?.path === normalizeStateRoutePath(targetUrl)
      ? previousEntry.url
      : null;
  }

  public resolveBackUrl(currentUrl: string, fallbackUrl: string): string {
    const currentRoutes: readonly StateRouteEntry[] = this.routesSignal();
    const currentEntry: StateRouteEntry | undefined = currentRoutes[currentRoutes.length - 1];
    if (
      currentEntry
      && (
        isStateRouteTransient(currentUrl)
        || currentEntry.path !== normalizeStateRoutePath(currentUrl)
      )
    ) {
      return currentEntry.url;
    }
    return currentRoutes.length > 1
      ? currentRoutes[currentRoutes.length - 2].url
      : fallbackUrl;
  }

  private resolveSameBranchBase(
    routes: readonly StateRouteEntry[],
    parentPaths: readonly string[],
  ): readonly StateRouteEntry[] {
    for (let index: number = parentPaths.length - 1; index >= 0; index--) {
      const parentIndex: number = routes.findIndex(
        (route: StateRouteEntry): boolean => route.path === parentPaths[index],
      );
      if (parentIndex >= 0) return routes.slice(0, parentIndex + 1);
    }
    return routes;
  }

  private setRoutes(routes: readonly StateRouteEntry[]): void {
    const normalizedRoutes: readonly StateRouteEntry[] = this.limitRoutes(routes);
    this.routesSignal.set(normalizedRoutes);
    this.persist();
  }

  private limitRoutes(routes: readonly StateRouteEntry[]): readonly StateRouteEntry[] {
    const rootEntry: StateRouteEntry = createStateRouteEntry(STATE_ROUTE_ROOT);
    const routesWithoutRoot: StateRouteEntry[] = routes.filter(
      (route: StateRouteEntry): boolean => normalizeStateRoutePath(route.path) !== STATE_ROUTE_ROOT,
    );
    return [rootEntry, ...routesWithoutRoot.slice(-(MAX_STATE_ROUTE_DEPTH - 1))];
  }

  private restore(): void {
    try {
      const value: unknown = this.sessionStorage.getItem(StateRouteStore.STORAGE_KEY, true);
      if (value === null) return;
      if (!this.isPersistedState(value)) {
        this.clearPersistedState();
        return;
      }

      this.ownerUserId = value.ownerUserId;
      this.sessionIdValue = value.sessionId;
      this.routesSignal.set(this.limitRoutes(value.routes));
    } catch {
      this.clearPersistedState();
    }
  }

  private persist(): void {
    if (this.ownerUserId === undefined) return;

    const state: PersistedStateRouteState = {
      ownerUserId: this.ownerUserId,
      routes: this.routesSignal(),
      sessionId: this.sessionIdValue,
    };
    try {
      this.sessionStorage.setItem(StateRouteStore.STORAGE_KEY, state);
    } catch {
      return;
    }
  }

  private isPersistedState(value: unknown): value is PersistedStateRouteState {
    if (!this.isRecord(value)) return false;
    if (typeof value['sessionId'] !== 'string' || !value['sessionId']) return false;
    if (value['ownerUserId'] !== null && !this.isPositiveInteger(value['ownerUserId'])) return false;
    if (!Array.isArray(value['routes']) || !value['routes'].every(
      (route: unknown): route is StateRouteEntry => this.isStateRouteEntry(route),
    )) return false;
    return true;
  }

  private isStateRouteEntry(value: unknown): value is StateRouteEntry {
    if (!this.isRecord(value)) return false;
    if (typeof value['path'] !== 'string' || typeof value['url'] !== 'string') return false;
    if (!isInternalStateRouteUrl(value['path']) || !isInternalStateRouteUrl(value['url'])) return false;
    return value['path'] === normalizeStateRoutePath(value['path'])
      && value['url'] === createStateRouteEntry(value['url']).url
      && value['path'] === normalizeStateRoutePath(value['url']);
  }

  private isPositiveInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private clearPersistedState(): void {
    try {
      this.sessionStorage.removeItem(StateRouteStore.STORAGE_KEY);
    } catch {
      return;
    }
  }

  private createSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
